import React, { useEffect, useMemo, useState } from "react";
import "./update-product-status.modal.scoped.scss";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import { icons } from "../../../../statics/icons";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { ProductStatus } from "../../types/status";
import { ProductsService } from "../../../../services/products.services";
import { showPopupToastMsg, showToastMsg } from "../../../../helpers";

interface UpdateProductStatusModalProps {
  onClose: any;
  show: boolean;
  dialogClassName?: string;
  productId: string;
  status: string;
}

const UpdateProductStatusModal = (props: UpdateProductStatusModalProps) => {
  const handleClose = (value: any) => props.onClose(value);
  const { t } = useTranslation();
  const productsService = useMemo(() => new ProductsService(), []);

  const [selectedStatus, setSelectedStatus] = useState(
    props.status || "NOT_SELECTED",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (props.show) setSelectedStatus(props.status || "NOT_SELECTED");
  }, [props.show, props.status]);

  const handleUpdate = async (e: any) => {
    setSaving(true);
    try {
      const response = await productsService.updateStatus(
        props.productId,
        selectedStatus,
      );
      if (response && response.message === "SUCCESS") {
        showToastMsg(
          "success",
          t("PRODUCTS_PAGE.PRODUCT_STATUS_UPDATED_SUCCESSFULLY"),
        );
        handleClose(true);
      } else {
        showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      }
    } catch (error) {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={props.show}
      onHide={handleClose.bind(null, false)}
      centered
      dialogClassName={
        props.dialogClassName ? props.dialogClassName : "modal-lg"
      }
    >
      <Modal.Header
        closeButton
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <Modal.Title>{t("PRODUCTS_PAGE.UPDATE_PRODUCT_STATUS")}</Modal.Title>
      </Modal.Header>
      <Box
        className="d-flex flex-column gap-2 align-items-center justify-content-center p-2"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <img
          src={icons.HEAD_DIALOG}
          alt={t("PRODUCTS_PAGE.UPDATE_PRODUCT_STATUS")}
          width={180}
        />
      </Box>
      <Modal.Body>
        <Box className="title">
          <Typography variant="h4">
            {t("PRODUCTS_PAGE.CHANGE_PRODUCT_STATUS_TITLE")}
          </Typography>
          <Typography variant="body1">
            {t("PRODUCTS_PAGE.CHANGE_PRODUCT_STATUS_DESCRIPTION")}
          </Typography>
        </Box>
        <Box className="status__form mt-3">
          <FormControl component="fieldset">
            <RadioGroup
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{ flexDirection: "column" }}
            >
              {Object.values(ProductStatus).map(
                (status: any, index: number) => (
                  <FormControlLabel
                    key={index}
                    value={status?.name}
                    control={<Radio color="primary" />}
                    label={t(status.name)}
                  />
                ),
              )}
            </RadioGroup>
          </FormControl>
        </Box>
        <Box className="actions">
          <Button
            variant="outlined"
            color="primary"
            className="btn-secondary-theme"
            onClick={handleClose.bind(null, false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className="btn-primary-theme"
            onClick={handleUpdate}
            disabled={selectedStatus === props.status || saving}
          >
            {saving ? (
              <CircularProgress color="inherit" size={30} />
            ) : (
              <>{t("UPDATE")}</>
            )}
          </Button>
        </Box>
      </Modal.Body>
    </Modal>
  );
};

export default UpdateProductStatusModal;
